import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "./utils";

interface PopoverContextType {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLElement | null>;
}

const PopoverContext = React.createContext<PopoverContextType | null>(null);

function Popover({
  open,
  onOpenChange,
  children,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}) {
  const [internalOpen, setInternalOpen] = React.useState(false);

  const triggerRef = React.useRef<HTMLElement>(null);

  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      onOpenChange?.(newOpen);
      if (!isControlled) {
        setInternalOpen(newOpen);
      }
    },
    [onOpenChange, isControlled],
  );

  return (
    <PopoverContext.Provider
      value={{
        open: isOpen,
        onOpenChange: handleOpenChange,
        triggerRef,
      }}
    >
      {children}
    </PopoverContext.Provider>
  );
}

function PopoverTrigger({
  children,
  asChild,
  ...props
}: React.HTMLAttributes<HTMLElement> & {
  asChild?: boolean;
}) {
  const context = React.useContext(PopoverContext);

  if (!context) {
    throw new Error("PopoverTrigger must be used within Popover");
  }

  const handleClick = () => {
    context.onOpenChange(!context.open);
  };

  if (asChild && React.isValidElement(children)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return React.cloneElement(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      children as React.ReactElement<any>,
      {
        ref: context.triggerRef,
        ...props,
        onClick: handleClick,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any,
    );
  }

  return (
    <button
      ref={context.triggerRef as React.RefObject<HTMLButtonElement>}
      onClick={handleClick}
      {...props}
    >
      {children}
    </button>
  );
}

function PopoverContent({
  className,
  children,
  portalled = true,
  side = "bottom",
  sideOffset = 4,
  collisionPadding = 10,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  portalled?: boolean;
  side?: "top" | "bottom";
  sideOffset?: number;
  collisionPadding?: number;
}) {
  const context = React.useContext(PopoverContext);

  if (!context) {
    throw new Error("PopoverContent must be used within Popover");
  }

  const [style, setStyle] = React.useState<React.CSSProperties>({});
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!context.open) return;

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;

      const clickedTrigger = context.triggerRef.current?.contains(target);

      const clickedContent = contentRef.current?.contains(target);

      if (!clickedTrigger && !clickedContent) {
        context.onOpenChange(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.open, context.onOpenChange]);

  React.useLayoutEffect(() => {
    if (!context.open || !context.triggerRef.current) {
      return;
    }

    const updatePosition = () => {
      const trigger = context.triggerRef.current;
      const content = contentRef.current;

      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      if (!portalled) {
        setStyle({
          position: "absolute",
          top: `calc(100% + ${sideOffset}px)`,
          left: 0,
          width: "100%",
          zIndex: 9999,
        });

        return;
      }

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const contentHeight = content?.getBoundingClientRect().height ?? 300;

      const contentWidth = content?.getBoundingClientRect().width ?? rect.width;

      const spaceAbove = rect.top - collisionPadding;

      const spaceBelow = viewportHeight - rect.bottom - collisionPadding;

      const shouldOpenAbove =
        side === "top" ||
        (side === "bottom" &&
          spaceBelow < contentHeight + sideOffset + collisionPadding &&
          spaceAbove >= contentHeight + sideOffset);

      let top: number;

      if (shouldOpenAbove) {
        top = rect.top - contentHeight - sideOffset;

        top = Math.max(collisionPadding, top);
      } else {
        top = rect.bottom + sideOffset;

        top = Math.min(top, viewportHeight - contentHeight - collisionPadding);
      }

      let left = rect.left;

      if (left + contentWidth > viewportWidth - collisionPadding) {
        left = viewportWidth - contentWidth - collisionPadding;
      }

      left = Math.max(collisionPadding, left);

      setStyle({
        position: "fixed",
        top,
        left,
        width: rect.width,
        zIndex: 9999,
        maxHeight: `calc(100vh - ${collisionPadding * 2}px)`,
        overflow: "hidden",
      });
    };

    updatePosition();
    const frame1 = requestAnimationFrame(updatePosition);

    const frame2 = requestAnimationFrame(() => {
      requestAnimationFrame(updatePosition);
    });
    window.addEventListener("resize", updatePosition);

    window.addEventListener("scroll", updatePosition, true);
    const resizeObserver = contentRef.current
      ? new ResizeObserver(updatePosition)
      : null;

    if (resizeObserver && contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      cancelAnimationFrame(frame1);
      cancelAnimationFrame(frame2);

      window.removeEventListener("resize", updatePosition);

      window.removeEventListener("scroll", updatePosition, true);

      resizeObserver?.disconnect();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.open, portalled, side, sideOffset, collisionPadding, children]);

  if (!context.open) {
    return null;
  }

  const content = (
    <div
      ref={contentRef}
      style={style}
      className={cn(
        "rounded-md border bg-white shadow-lg animate-in fade-in-0 zoom-in-95",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );

  return portalled ? createPortal(content, document.body) : content;
}

export { Popover, PopoverTrigger, PopoverContent };
